import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { animated, useSpring, useTransition } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import * as Yup from 'yup';
import IcomoonReact from 'icomoon-react';
import { Form } from 'formik';

import CreateProgramSlide1 from './CreateProgramSlide1';
import CreateProgramSlide2 from './CreateProgramSlide2';
import CreateProgramSlide3 from './CreateProgramSlide3';
import NoHoverButton from './NoHoverButton';

import FormHandler from '../../../../../shared/components/FormHandler/FormHandler';
import Spinner from '../../../../../shared/_TBComponents/Spinner';
import Text from '../../../../../shared/_TBComponents/Text';
import { TransitionGlobal } from '../../../../../shared/GlobalStyles';
import iconSet from '../../../../../shared/images/teambuildr-selection.json';
import useReseller from '../../hooks/useReseller';

const PAGE_1_FIELDS = [
  'programName',
  'programUrl',
  'shortDescription',
  'longDescription',
  'redirectUrl',
];

const PAGE_2_FIELDS = ['programType', 'calendarOffsetDate'];

const PAGE_3_FIELDS = ['price', 'billingFrequency', 'agreement'];

const DEFAULT_SLIDE_STYLE = { position: 'absolute', width: '100%' };

const FieldError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin: 10px 0px;
`;

const FolderTextWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: -10px;
  min-height: 78px;
  min-width: 250px;
  width: 40%;

  .calendar-icon-text {
    display: flex;
    font-size: 26px;
    font-weight: 300;
    justify-content: center;
    min-width: 300px;
  }
`;

const InnerFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  .divider-line {
    border-top: 2px solid rgb(232, 232, 232);
    display: flex;
    margin-bottom: 10px;
    margin-top: 5px;
    width: calc(100% + 80px);
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const NavCircle = styled('div')`
  border: 1px solid ${(props) => props.theme.colors.border || '#979797'};
  border-radius: 100%;
  cursor: pointer;
  display: block;
  height: 10px;
  width: 10px;

  &.active {
    background-color: ${(props) => props.theme.colors.gray || '#d8d8d8'};
    border: 1px solid ${(props) => props.theme.colors.gray || '#d8d8d8'};
    cursor: default;
  }
`;

const NavigationWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  left: 0px;
  margin-bottom: 30px;
  padding: 20px;
  position: absolute;
  top: 0px;
  width: 100%;
  z-index: 1;

  .arrow-div {
    cursor: pointer;
  }
`;

const SlideContainer = styled('div')`
  display: block;
  height: ${(props) => `${props.height}px`};
  min-height: 184px;
  position: relative;
  transition: ${TransitionGlobal};
  width: 100%;
  z-index: 1000;
`;

const SliderNav = styled('div')`
  align-items: center;
  display: flex;
  height: 30px;
  justify-content: space-between;
  margin: 0 auto;
  position: relative;
  width: 45px;
  z-index: 10;
`;

const SpinnerContainer = styled('div')`
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
  left: 0;
  margin-top: 40px;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`;

const TitleError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  display: flex;
  font-size: 14px;
  justify-content: center;
  width: 500px;
`;

const programSchema = Yup.object().shape({
  agreement: Yup.boolean()
    .required('Checkbox below is required')
    .oneOf([true], 'Checkbox below is required'),
  billingFrequency: Yup.string().required('Required'),
  calendarOffsetDate: Yup.string().when('programType', {
    is: (programType) => programType === 'PRE-BUILT',
    then: Yup.string().required('Required'),
    otherwise: Yup.string(),
  }),
  longDescription: Yup.string().required('Description is required'),
  price: Yup.string().required('Required'),
  programName: Yup.string().required('Name is required'),
  programType: Yup.string()
    .when('calendarId', {
      is: (calId) => calId,
      then: Yup.string().required('Required'),
      otherwise: Yup.string(),
    })
    .nullable(),
  programUrl: Yup.string().required('A program URL is required'),
  redirectUrl: Yup.string().url('Must be a valid URL'),
  shortDescription: Yup.string().required('Tagline is required'),
});

const CreateProgramForm = ({ onRequestClose }) => {
  const theme = useTheme();

  const [activeSlide, setActiveSlide] = useState(0);
  const [isMediaUploaded, setIsMediaUploaded] = useState(false);
  const [page1Errors, setPage1Errors] = useState(false);
  const [page2Errors, setPage2Errors] = useState(false);
  const [page3Errors, setPage3Errors] = useState(false);
  const [prevSlide, setPrevSlide] = useState(0);
  const [programSource, setProgramSource] = useState({
    label: 'Calendar',
    value: 'calendar',
  });
  const [programType, setProgramType] = useState(null);
  const [savedWorkoutError, setSavedWorkoutError] = useState('');
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedSavedWorkouts, setSelectedSavedWorkouts] = useState([]);
  const [slideDimensions, setSlideDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [submitClicked, setSubmitClicked] = useState(false);
  const [uploadedMediaName, setUploadedMediaName] = useState('');
  const [urlHandled, setUrlHandled] = useState(false);

  const slideRef = useRef();

  const {
    currentUser,
    handleCreateProgram,
    handleFetchCalendars,
    handleFetchSavedWorkouts,
    handleFetchUserGroups,
    userGroups,
  } = useReseller();

  const createProgramError = useSelector(
    (state) => state.reseller.data.createProgramError,
  );
  const isProgramCreating = useSelector(
    (state) => state.reseller.ui.isProgramCreating,
  );
  const uploadedMedia = useSelector((state) => state.reseller.data.uploadMedia);

  useEffect(() => {
    const accountCode = currentUser?.accountCode;
    if (accountCode && !userGroups) {
      handleFetchUserGroups(accountCode);
      handleFetchCalendars(accountCode);
      handleFetchSavedWorkouts(accountCode);
    }
  }, [
    currentUser,
    handleFetchUserGroups,
    handleFetchCalendars,
    handleFetchSavedWorkouts,
    userGroups,
  ]);

  useEffect(() => {
    if (
      programSource.value === 'savedWorkout'
      && selectedSavedWorkouts?.length > 0
      && savedWorkoutError
    ) {
      setSavedWorkoutError('');
    }
  }, [selectedSavedWorkouts, programSource.value, savedWorkoutError]);

  useEffect(() => {
    if (activeSlide !== 1 && savedWorkoutError) {
      setSavedWorkoutError('');
    }
  }, [activeSlide, savedWorkoutError]);

  const formAnimation = useSpring({
    opacity: 1,
    zIndex: 1,
  });

  const spinnerAnimation = useSpring({ opacity: 0, zIndex: 0 });

  const fromResolver = () => {
    if (prevSlide === 0 && activeSlide === 1) {
      return 'translate3d(25%, 0, 0)';
    }
    if (prevSlide === 1 && activeSlide === 2) {
      return 'translate3d(25%, 0, 0)';
    }
    return 'translate3d(-25%, 0, 0)';
  };

  const leaveResolver = () => {
    if (prevSlide === 2 && activeSlide === 1) {
      return 'translate3d(-25%, 0, 0)';
    }
    if (prevSlide === 1 && activeSlide === 2) {
      return 'translate3d(-25%, 0, 0)';
    }
    if (prevSlide === 0 && activeSlide === 1) {
      return 'translate3d(-25%, 0, 0)';
    }
    return 'translate3d(25%, 0, 0)';
  };

  const slideTransition = useTransition(activeSlide, null, {
    enter: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    from: { opacity: 0, transform: fromResolver() },
    leave: { opacity: 0, transform: leaveResolver() },
  });

  const getPage2Fields = (formValues) => {
    const hasCalendarId = formValues.calendarId !== ''
      && formValues.calendarId !== undefined;
    return hasCalendarId ? PAGE_2_FIELDS : [];
  };

  const validateSavedWorkouts = (formValues) => {
    if (programSource.value === 'savedWorkout') {
      const { savedWorkoutIds } = formValues;
      if (!savedWorkoutIds || savedWorkoutIds.length === 0) {
        setSavedWorkoutError('You must select at least one saved workout');
        setPage2Errors(true);
        return false;
      }
    }
    return true;
  };

  const validatePageFields = (errors, fieldsToCheck) => Object.keys(errors).some(
    (errorKey) => fieldsToCheck.includes(errorKey),
  );

  const handleBackNavigation = () => {
    if (activeSlide === 1) {
      setActiveSlide(0);
    } else if (activeSlide === 2) {
      setActiveSlide(1);
    }
  };

  const handleClose = () => {
    onRequestClose();
  };

  const handleNavToSlide1 = (formProps) => {
    if (activeSlide === 0) {
      formProps.validateForm().then((errors) => {
        if (validatePageFields(errors, PAGE_1_FIELDS)) {
          setPage1Errors(true);
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(1);
        }
      });
    } else {
      setPrevSlide(activeSlide);
      setActiveSlide(1);
    }
  };

  const handleNavToSlide2 = (formProps) => {
    if (activeSlide === 0 || activeSlide === 1) {
      formProps.validateForm().then((errors) => {
        const fieldsToCheck = activeSlide === 0 ? PAGE_1_FIELDS : getPage2Fields(formProps.values);

        if (!validateSavedWorkouts(formProps.values)) {
          if (activeSlide === 0) {
            setPrevSlide(activeSlide);
            setActiveSlide(1);
          }
          return;
        }

        if (validatePageFields(errors, fieldsToCheck)) {
          if (activeSlide === 0) {
            setPage1Errors(true);
          } else {
            setPage2Errors(true);
          }
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(2);
        }
      });
    } else {
      setPrevSlide(activeSlide);
      setActiveSlide(2);
    }
  };

  const handleNextStep = (formProps) => {
    if (activeSlide === 0) {
      formProps.validateForm().then((errors) => {
        if (validatePageFields(errors, PAGE_1_FIELDS)) {
          setPage1Errors(true);
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(1);
        }
      });
    } else if (activeSlide === 1) {
      formProps.validateForm().then((errors) => {
        const page2Fields = getPage2Fields(formProps.values);

        if (!validateSavedWorkouts(formProps.values)) {
          return;
        }

        if (validatePageFields(errors, page2Fields)) {
          setPage2Errors(true);
        } else {
          setPrevSlide(activeSlide);
          setActiveSlide(2);
        }
      });
    }
  };

  const handleSubmit = (formProps) => {
    formProps.validateForm().then((errors) => {
      if (validatePageFields(errors, PAGE_3_FIELDS)) {
        setPage3Errors(true);
      } else {
        formProps.setSubmitting(true);
        formProps.submitForm();
        setSubmitClicked(true);
      }
    });
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    setTimeout(() => {
      const hasSavedWorkoutIds = values.savedWorkoutIds?.length > 0;
      const newValues = {
        autoStartProgram: hasSavedWorkoutIds ? values.autoStartProgram : false,
        billingFrequency: values.billingFrequency,
        calendarId: values.calendarId || 0,
        descriptionLong: values.longDescription,
        descriptionShort: values.shortDescription,
        groups: values.groups || [],
        mediaId: uploadedMedia?.id || 0,
        price: Math.round(values.price * 100),
        programName: values.programName,
        redirectUrl: values.redirectUrl || '',
        saveInfoIds: values.savedWorkoutIds || [],
        status: values.status,
        urlSlug: values.programUrl,
      };

      if (values.numBillingCycles) {
        newValues.numBillingCycles = values.numBillingCycles.id;
      }

      if (values.mondayStart) {
        newValues.startOnDayOfWeek = 1;
      }

      if (values.programType === 'PRE-BUILT') {
        newValues.calendarOffsetDate = values.calendarOffsetDate || '';
      }

      if (values.trialPeriod) {
        newValues.trialPeriod = values.trialPeriod.id;
      } else {
        newValues.trialPeriod = 0;
      }

      handleCreateProgram({
        accountCode: currentUser.accountCode,
        newProgram: newValues,
      });
      setSubmitting(false);
    });
  };

  const renderSlide = (item, props, formProps) => {
    const slideProps = {
      ref: slideRef,
      style: { ...DEFAULT_SLIDE_STYLE, ...props },
    };

    if (item === 0) {
      return (
        <animated.div {...slideProps}>
          <ResizeObserver
            onResize={(rect) => {
              setSlideDimensions(rect);
            }}
          />
          <CreateProgramSlide1
            currentUser={currentUser}
            formProps={formProps}
            isMediaUploaded={isMediaUploaded}
            page1Errors={page1Errors}
            setIsMediaUploaded={setIsMediaUploaded}
            setUploadedMediaName={setUploadedMediaName}
            setUrlHandled={setUrlHandled}
            uploadedMediaName={uploadedMediaName}
            urlHandled={urlHandled}
          />
        </animated.div>
      );
    }

    if (item === 1) {
      return (
        <animated.div {...slideProps}>
          <ResizeObserver
            onResize={(rect) => {
              setSlideDimensions(rect);
            }}
          />
          <CreateProgramSlide2
            formProps={formProps}
            page2Errors={page2Errors}
            programSource={programSource}
            programType={programType}
            savedWorkoutError={savedWorkoutError}
            selectedCalendar={selectedCalendar}
            selectedDate={selectedDate}
            selectedGroups={selectedGroups}
            selectedSavedWorkouts={selectedSavedWorkouts}
            setProgramSource={setProgramSource}
            setProgramType={setProgramType}
            setSavedWorkoutError={setSavedWorkoutError}
            setSelectedCalendar={setSelectedCalendar}
            setSelectedDate={setSelectedDate}
            setSelectedGroups={setSelectedGroups}
            setSelectedSavedWorkouts={setSelectedSavedWorkouts}
          />
        </animated.div>
      );
    }

    if (item === 2) {
      return (
        <animated.div {...slideProps}>
          <ResizeObserver
            onResize={(rect) => {
              setSlideDimensions(rect);
            }}
          />
          <CreateProgramSlide3
            formProps={formProps}
            page3Errors={page3Errors}
            programSource={programSource}
          />
        </animated.div>
      );
    }

    return null;
  };

  return (
    <ModalWrapper>
      <NavigationWrapper>
        {(activeSlide === 1 || activeSlide === 2) ? (
          <Text className='back arrow-div' onClick={handleBackNavigation}>
            <IcomoonReact iconSet={iconSet} icon='left-arrow' size={15} />
          </Text>
        ) : <div />}
        <Text className='close arrow-div' onClick={handleClose}>
          <IcomoonReact iconSet={iconSet} icon='remove' size={13} />
        </Text>
      </NavigationWrapper>
      <FormHandler
        initialValues={{
          autoStartProgram: true,
          billingFrequency: 1,
          longDescription: '',
          price: '',
          programName: '',
          programUrl: '',
          shortDescription: '',
          status: currentUser?.organizationSubscriptionStatus === 'ACTIVE' ? 1 : 0,
        }}
        validationSchema={programSchema}
        onSubmit={handleFormSubmit}
      >
        {(formProps) => (
          <InnerFormWrapper>
            <FolderTextWrapper>
              <Text className='calendar-icon-text'>Add New Program</Text>
              {Object.keys(formProps.errors).length > 0 && submitClicked && (
                <TitleError className='error-text'>
                  Some required fields are blank
                </TitleError>
              )}
              {createProgramError && (
                <TitleError className='error-text'>
                  {createProgramError}
                </TitleError>
              )}
            </FolderTextWrapper>
            <div className='divider-line' />
            <Form style={{ width: '100%' }}>
              <animated.div style={formAnimation}>
                {formProps.errors.exercise && formProps.touched.exercise && (
                  <FieldError className='text-center'>
                    {formProps.errors.exercise}
                  </FieldError>
                )}
                <SlideContainer height={slideDimensions.height}>
                  {slideTransition.map(({ item, props }) => renderSlide(item, props, formProps))}
                </SlideContainer>
                <SliderNav>
                  <NavCircle
                    className={activeSlide === 0 ? 'active' : ''}
                    onClick={() => {
                      setPrevSlide(activeSlide);
                      setActiveSlide(0);
                    }}
                  />
                  <NavCircle
                    className={activeSlide === 1 ? 'active' : ''}
                    onClick={() => handleNavToSlide1(formProps)}
                  />
                  <NavCircle
                    className={activeSlide === 2 ? 'active' : ''}
                    onClick={() => handleNavToSlide2(formProps)}
                  />
                </SliderNav>
              </animated.div>
              <animated.div style={spinnerAnimation}>
                <SpinnerContainer>
                  <Spinner large />
                </SpinnerContainer>
              </animated.div>
              {activeSlide === 2 && (
                <NoHoverButton
                  bottom
                  className='modal-button2'
                  customColor={theme.colors.green}
                  cta='Submit'
                  disabled={isProgramCreating}
                  fullWidth
                  large
                  noBorder
                  onClick={() => handleSubmit(formProps)}
                  primary
                  square
                  type='button'
                />
              )}
              {(activeSlide === 0 || activeSlide === 1) && (
                <NoHoverButton
                  bottom
                  className='modal-button2'
                  customColor={theme.colors.green}
                  cta='Next Step'
                  fullWidth
                  large
                  noBorder
                  onClick={() => handleNextStep(formProps)}
                  primary
                  square
                  type='button'
                />
              )}
            </Form>
          </InnerFormWrapper>
        )}
      </FormHandler>
    </ModalWrapper>
  );
};

CreateProgramForm.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default CreateProgramForm;
